/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      component — slide-in mobile navigation menu (index and user routes)
 *   extends:   Component (@glimmer/component)
 *   template:  mobile-menu.hbs
 *   args:      @setShowMenu
 *   uses:      session/router services
 *   siblings:  -
 *   tests:     mobile-menu-navigation-test.js, user-area-reads-test.js
 *   gotchas:   goto() guards against the DOM event {{on}} appends as the next
 *              positional arg leaking in as `tab` when the caller omits it.
 *
 * Slide-in menu shown on small screens (index and user routes).
 * Always lists the logged-in sections (my data, appointments, invoices,
 * receipts) so they stay reachable from any page, not only from the user area.
 */
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { action } from '@ember/object';

export default class MobileMenuComponent extends Component {
  @service session;
  @service router;

  /**
   * Navigates by asking the router, not by letting an <a href> be followed.
   * The rows used to be `LinkTo`s, which only stay inside the app as long as
   * their click handler runs and calls `preventDefault()`; anything that keeps
   * it from running turns the row into a plain browser navigation, i.e. a full
   * page reload. A <button> has no default navigation to leak.
   *
   * The trailing DOM event `{{on}}` appends to the arguments is ignored on
   * purpose.
   */
  @action
  goto(route, tab = null) {
    // Guard against the DOM event leaking in as the tab when the caller omits it
    const target = tab instanceof Event ? null : tab;

    this.args.setShowMenu?.(null);
    this.router.transitionTo(route, { queryParams: { tab: target } });
  }

  @action
  gotoIndex() {
    this.args.setShowMenu?.(null);
    this.router.transitionTo('index');
  }

  get bookingSettings() {
    return this.session.option_field?.booking_settings ?? {};
  }

  get isLogged() {
    return !!this.session.me?.id;
  }

  get hideEvent() {
    return !!this.bookingSettings.hide_event_list;
  }

  get hideInvoice() {
    return !!this.bookingSettings.hide_invoice_list;
  }

  get hideReceipt() {
    return !!this.bookingSettings.hide_receipt_list;
  }
}
